import React from 'react';
import Card from '../../components/ui/Card';
import partenairesData from '../../data/partenairesData.json';
import { ArrowTopRightOnSquareIcon, MapPinIcon } from '@heroicons/react/24/outline';

export default function LogementPage() {
  const associations = partenairesData.associations || [];
  const etablissements = partenairesData.etablissements || [];
  const personnes = partenairesData.personnes || [];

  return (
    <div className="max-w-5xl mx-auto space-y-10 animate-fade-in px-4">
      <div className="text-center space-y-2 max-w-3xl mx-auto">
        <h1 className="font-title text-3xl sm:text-4xl font-extrabold text-acem-primary-dark">
          Nos Partenaires & Soutiens
        </h1>
        <p className="text-sm sm:text-base text-acem-muted">
          Découvrez les associations, institutions académiques et personnalités ressources qui accompagnent l'ACEM Fès au quotidien.
        </p>
      </div>

      {/* Associations */}
      <section className="space-y-4">
        <div className="border-b border-acem-border pb-2">
          <h2 className="font-title text-xl font-bold text-acem-primary-dark">
            Associations Partenaires
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {associations.map((asso, idx) => (
            <Card key={idx} variant="elevated" className="p-5 sm:p-6 flex flex-col justify-between space-y-4 hover:shadow-acem-lg transition-all duration-300">
              <div className="space-y-3 flex-1">
                <div className="flex items-start gap-3.5 sm:gap-4">
                  {asso.image && asso.image !== false && (
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl bg-white border border-acem-border/70 p-2 flex items-center justify-center flex-shrink-0 shadow-sm">
                      <img
                        src={asso.image}
                        alt={asso.nom}
                        className="w-full h-full object-contain"
                        loading="lazy"
                        onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
                      />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-title text-base sm:text-lg font-bold text-acem-primary-dark leading-snug">
                      {asso.nom}
                    </h3>
                    {asso.nomAr && (
                      <p className="text-xs font-bold text-acem-muted font-sans mt-0.5">
                        {asso.nomAr}
                      </p>
                    )}
                    {asso.type && (
                      <span className="inline-block mt-1 text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-acem-primary/10 text-acem-primary border border-acem-primary/20 uppercase tracking-wide">
                        {asso.type}
                      </span>
                    )}
                  </div>
                </div>

                {asso.adresse && (
                  <p className="text-xs text-acem-muted flex items-center gap-1.5 font-medium">
                    <MapPinIcon className="w-4 h-4 text-acem-primary flex-shrink-0" aria-hidden="true" />
                    <span>{asso.adresse}</span>
                  </p>
                )}

                <p className="text-xs sm:text-sm text-acem-text/80 leading-relaxed pt-1">
                  {asso.desc}
                </p>
              </div>

              {asso.siteWeb && asso.siteWeb !== false && (
                <div className="pt-3 border-t border-acem-border/50 flex justify-end">
                  <a
                    href={asso.siteWeb}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold bg-acem-primary text-white hover:bg-acem-primary-dark transition-all duration-200 shadow-sm hover:shadow hover:-translate-y-0.5 group"
                  >
                    <span>Visiter le site</span>
                    <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden="true" />
                  </a>
                </div>
              )}
            </Card>
          ))}
        </div>
      </section>

      {/* Établissements */}
      <section className="space-y-4">
        <div className="border-b border-acem-border pb-2">
          <h2 className="font-title text-xl font-bold text-acem-primary-dark">
            Établissements & Centres Partenaires
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {etablissements.map((etab, idx) => (
            <Card key={idx} variant="elevated" className="p-5 sm:p-6 flex flex-col justify-between space-y-4 hover:shadow-acem-lg transition-all duration-300">
              <div className="space-y-3 flex-1">
                <div className="flex items-start gap-3.5 sm:gap-4">
                  {etab.image && etab.image !== false && (
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl bg-white border border-acem-border/70 p-2 flex items-center justify-center flex-shrink-0 shadow-sm">
                      <img
                        src={etab.image}
                        alt={etab.nom}
                        className="w-full h-full object-contain"
                        loading="lazy"
                        onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
                      />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-title text-base sm:text-lg font-bold text-acem-primary-dark leading-snug">
                      {etab.nom}
                    </h3>
                    {etab.nomAr && (
                      <p className="text-xs font-bold text-acem-muted font-sans mt-0.5">
                        {etab.nomAr}
                      </p>
                    )}
                    {etab.type && (
                      <span className="inline-block mt-1 text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-acem-primary/10 text-acem-primary border border-acem-primary/20 uppercase tracking-wide">
                        {etab.type}
                      </span>
                    )}
                  </div>
                </div>

                <p className="text-xs sm:text-sm text-acem-text/80 leading-relaxed pt-1">
                  {etab.desc}
                </p>
              </div>

              {etab.siteWeb && etab.siteWeb !== false && (
                <div className="pt-3 border-t border-acem-border/50 flex justify-end">
                  <a
                    href={etab.siteWeb}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold bg-acem-primary text-white hover:bg-acem-primary-dark transition-all duration-200 shadow-sm hover:shadow hover:-translate-y-0.5 group"
                  >
                    <span>Visiter le site</span>
                    <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden="true" />
                  </a>
                </div>
              )}
            </Card>
          ))}
        </div>
      </section>

      {/* Personnes ressources */}
      <section className="space-y-4">
        <div className="border-b border-acem-border pb-2">
          <h2 className="font-title text-xl font-bold text-acem-primary-dark">
            Personnes Ressources
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {personnes.map((pers, idx) => (
            <Card key={idx} variant="elevated" className="p-5 sm:p-6 flex flex-col justify-between space-y-4 hover:shadow-acem-lg transition-all duration-300">
              <div className="space-y-3 flex-1">
                <div className="flex items-start gap-3.5 sm:gap-4">
                  {pers.image && pers.image !== false && (
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl overflow-hidden border-2 border-acem-border/80 flex-shrink-0 shadow-sm bg-acem-surface-container">
                      <img
                        src={pers.image}
                        alt={pers.nom}
                        className="w-full h-full object-cover object-top"
                        loading="lazy"
                        onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
                      />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-title text-base sm:text-lg font-bold text-acem-primary leading-snug">
                      {pers.nom}
                    </h3>
                  </div>
                </div>

                <p className="text-xs sm:text-sm text-acem-text/80 leading-relaxed pt-1">
                  {pers.desc}
                </p>
              </div>

              {pers.siteWeb && pers.siteWeb !== false && (
                <div className="pt-3 border-t border-acem-border/50 flex justify-end">
                  <a
                    href={pers.siteWeb}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold bg-acem-primary text-white hover:bg-acem-primary-dark transition-all duration-200 shadow-sm hover:shadow hover:-translate-y-0.5 group"
                  >
                    <span>Visiter le site</span>
                    <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden="true" />
                  </a>
                </div>
              )}
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
}
